import { useNinaApi } from '@data/api/NinaApi';
import { toUser } from '@data/dto/user.dto';
import { HttpErrorResponse } from '@data/models/error.interface';
import {
  LogoutHttp,
  USER_STORAGE,
  User,
  UserHttp,
} from '@data/models/user.interface';
import { useStorage } from '@plugins/storage';

export const TOKEN_SESSION_NAME = 'TOKEN_SESSION_NAME';

const HTTP_SERVICE_UNAVAILABLE = 503;

/**
 * Raised when Nina cannot build the current user because a dependency is down.
 */
export class ServiceUnavailableError extends Error {
  constructor() {
    super('ServiceUnavailable');
    this.name = 'ServiceUnavailableError';
  }
}

export const useAuthService = () => {
  const { get } = useNinaApi();
  const tokenSessionName = TOKEN_SESSION_NAME;
  const storage = useStorage();

  const getToken = (): string | null => {
    return storage.get<string>(tokenSessionName);
  };

  const setToken = (token: string): void => {
    return storage.set(tokenSessionName, token, true);
  };

  const initializeToken = (): void => {
    const url = new URL(window.location.toString());
    const apiToken = url.searchParams.get('scud_api_token');

    if (apiToken) {
      setToken(apiToken);
      url.searchParams.delete('scud_api_token');
      window.location.href = url.toString();
    }
  };

  const getMe = async (): Promise<User> => {
    const loginUri = `${process.env.VITE_APP_LOGIN_URL}?redirect_uri=${window.location.href}`;

    if (!getToken()) {
      window.location.href = loginUri;
      return toUser({}); // Return empty user to ensure no code is executed after this
    }

    const { data, error } = await get<{ data: UserHttp }>('/api/me');

    if (error) {
      // A dependency is down (eg. the agency perimeter cannot be read): keep the
      // session and let the app show a retry screen instead of looping on login.
      if (isServiceUnavailable(error)) {
        throw new ServiceUnavailableError();
      }

      if (error.meta?.redirect) {
        window.location.href = error.meta.redirect;
      } else {
        removeToken();
        window.location.href = loginUri;
      }
    }

    return toUser(data?.data || {});
  };

  /**
   * The HTTP status is the only reliable signal here: an infrastructure 503
   * (ingress, gateway) carries an HTML body with no JSON:API `errors` payload.
   * A request that got no response at all is an outage too, never a stale session.
   */
  const isServiceUnavailable = (error: HttpErrorResponse): boolean =>
    error.httpStatus === HTTP_SERVICE_UNAVAILABLE ||
    error.isNetworkError === true;

  const logout = async (): Promise<boolean> => {
    const { error } = await get<{ data: LogoutHttp }>('/api/v1/auth/logout');
    removeToken();
    removeUserRegions();

    return !error;
  };

  const removeToken = (): void => {
    storage.remove(tokenSessionName);
    storage.remove(USER_STORAGE);
  };

  const removeUserRegions = (): void => {
    storage.remove('user_regions');
  };

  return {
    logout,
    initializeToken,
    getMe,
    setToken,
    getToken,
  };
};
